import { useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table,
} from 'reactstrap';
import { Button as Btn } from 'src/components';
import DeleteInformationModal from 'src/components/DeleteInformation/DeleteInformationModal';
import {
  useDeleteKbQuestionMutation,
  useGetAdminKbQuestionsQuery,
} from 'src/rtk/features/chatbot/chatbotApi';

const PER_PAGE_OPTIONS = [10, 15, 20, 50];

const KnowledgeBaseListPage = () => {
  document.title = 'Knowledge Base - Questions | BCPS';
  const history = useHistory();
  const [params, setParams] = useState<AdminKbListParams>({
    page: 1,
    per_page: 10,
    keyword: '',
    status: '',
    sort: 'updated_at',
    order: 'desc',
  });
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [infoId, setInfoId] = useState<string | number>('');

  const { data: listData, isLoading } = useGetAdminKbQuestionsQuery(params);

  const [deleteQuestion, { isLoading: isDeleting }] = useDeleteKbQuestionMutation();

  const items = listData?.data?.data ?? [];
  const meta = listData?.data?.meta;

  const setParam = (key: keyof AdminKbListParams, value: unknown) => {
    setParams((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const deleteHandler = (open: boolean, id: string | number) => {
    setDeleteModalShow(open);
    setInfoId(id);
  };

  const handleConfirmDelete = (id: string) => {
    deleteQuestion(id)
      .unwrap()
      .then(() => {
        toast.success('Question deleted.');
        setDeleteModalShow(false);
      })
      .catch((e: any) => {
        toast.error(e?.data?.message || 'Failed to delete.');
      });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Card className="mb-4">
          <CardBody>
            <Row className="mb-3">
              <Col>
                <h5 className="mb-0">Knowledge Base - Questions & Answers</h5>
              </Col>
              <Col xs="auto">
                <Button
                  color="primary"
                  onClick={() => history.push('/admin/knowledge-base/questions/new')}
                >
                  New Question
                </Button>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={2}>
                <FormGroup>
                  <Label>Keyword</Label>
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={params.keyword ?? ''}
                    onChange={(e) => setParam('keyword', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && setParam('keyword', (e.target as HTMLInputElement).value)}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={params.status ?? ''}
                    onChange={(e) => setParam('status', e.target.value || undefined)}
                  >
                    <option value="">All</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </Input>
                </FormGroup>
              </Col>
              
              <Col md={2}>
                <FormGroup>
                  <Label>Per page</Label>
                  <Input
                    type="select"
                    value={params.per_page ?? 15}
                    onChange={(e) => setParam('per_page', Number(e.target.value))}
                  >
                    {PER_PAGE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Table className="table table-sm" responsive>
              <thead className="table-secondary">
                <tr>
                  <th>Sl.</th>
                  <th>Question</th>
                  <th>Answer</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7}>Loading...</td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No questions yet.{' '}
                      <Button color="link" className="p-0" onClick={() => history.push('/admin/knowledge-base/questions/new')}>
                        Create one
                      </Button>
                    </td>
                  </tr>
                ) : (
                  items.map((item: KbQuestion, index: number) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>
                        <span className="text-truncate d-inline-block text-wrap" style={{ maxWidth: 280 }}>
                          {item.question}
                        </span>
                      </td>
                      <td>
                        <span className="text-truncate d-inline-block" style={{ maxWidth: 280 }} title={item.answer ?? ''}>
                          {item.answer ?? '-'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge bg-${item.status === 'published' ? 'success' : 'secondary'}`}>
                          {item.status}
                        </span>
                      </td>
                      
                      <td>
                        <div className="d-flex gap-1 py-1">
                          <Btn
                            size="sm"
                            color="success"
                            type="button"
                            text="Edit"
                            onClick={() => history.push(`/admin/knowledge-base/questions/${item.id}/edit`)}
                          />
                          <Btn
                            size="sm"
                            text="Delete"
                            color="danger"
                            disabled={isDeleting}
                            isLoading={isDeleting}
                            onClick={() => deleteHandler(true, item.id)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {meta && meta.last_page > 1 && (
              <Pagination className="mt-2">
                <PaginationItem disabled={meta.current_page <= 1}>
                  <PaginationLink
                    previous
                    onClick={() => setParam('page', meta.current_page - 1)}
                    href="#"
                  />
                </PaginationItem>
                {Array.from({ length: meta.last_page }, (_, i) => i + 1)
                  .filter((p) => Math.abs(p - meta.current_page) <= 2 || p === 1 || p === meta.last_page)
                  .map((p) => (
                    <PaginationItem key={p} active={p === meta.current_page}>
                      <PaginationLink onClick={() => setParam('page', p)} href="#">
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                <PaginationItem disabled={meta.current_page >= meta.last_page}>
                  <PaginationLink next onClick={() => setParam('page', meta.current_page + 1)} href="#" />
                </PaginationItem>
              </Pagination>
            )}

            {deleteModalShow && (
              <DeleteInformationModal
                isOpen={deleteModalShow}
                toggle={() => setDeleteModalShow(false)}
                id={String(infoId)}
                deleteInfomation={handleConfirmDelete}
              />
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default KnowledgeBaseListPage;
